import type {
  Business as BusinessRecord,
  CompanyDetailsUpdate,
  EventEmailsSetting,
} from '@pulsify/shared/dto'
import { api, unwrap } from '~/shared/lib/rpc'
import { storageManager } from '~/shared/lib/storage-manager'
import type { Business } from '~/shared/types'

function toBusiness(record: BusinessRecord): Business {
  return {
    id: record.id,
    companyName: record.companyName,
    legalName: record.legalName,
    registrationNumber: record.registrationNumber ?? null,
    vatId: record.vatId ?? null,
    companyDetailsPending: record.companyDetailsPending ?? false,
    hasProfile: record.hasProfile ?? true,
  }
}

/**
 * The shape `/api/businesses/me` returns. Mirrors `businessMeResponseSchema`
 * from `@pulsify/shared/dto`. Only the `business` slice survives into the
 * dashboard's {@link Business} type today; `user` and `profile` are
 * available via dedicated auth/profile hooks elsewhere.
 */
interface BusinessMeResponse {
  user: { id: string; email: string; roles: string[] }
  profile: {
    id: string
    authUserId: string
    firstName: string
    lastName: string
    businessId: string
  }
  business: BusinessRecord | null
}

export const businessApi = {
  /**
   * Returns the current business record (id, companyName, legalName) from
   * `/api/businesses/me`. When an admin is viewing another business via the
   * admin-portal switcher, `?businessId=...` is appended (matches the
   * server-side businessMeQuerySchema). Self-view sends an empty query and
   * gets the admin's own business back.
   *
   * Listing-shaped fields (display name, hours, location, phone, ...) are
   * NOT on this endpoint. Pages that edit those fetch via
   * {@link usePrimaryListing} + {@link useUpdateListing} - the dashboard
   * "edit business" page is structurally an "edit primary listing" page,
   * and the type split makes that distinction visible at call sites.
   */
  async getMe(): Promise<Business> {
    const adminViewingBusinessId = storageManager.getAdminViewingBusinessId()
    // The backend mounts businessesRouter at /api/business-users (see
    // apps/backend/src/index.ts) - the kebab-case lives in the URL.
    const res = await api.api['business-users'].me.$get({
      query: adminViewingBusinessId ? { businessId: adminViewingBusinessId } : {},
    })
    const body = await unwrap<BusinessMeResponse>(res)
    if (!body.business) {
      throw new Error(
        'GET /businesses/me returned no business - the authenticated user has no business attached.',
      )
    }
    return toBusiness(body.business)
  },

  /** Saves the company details; a field left out stays as it is. */
  async updateCompanyDetails(businessId: string, input: CompanyDetailsUpdate): Promise<Business> {
    const res = await api.api['business-users'][':businessId'].company.$patch({
      param: { businessId },
      json: input,
    })
    return toBusiness(await unwrap<BusinessRecord>(res))
  },

  /** Whether the signed-in team member gets the Rückblick and milestone e-mails. */
  async getEventEmails(): Promise<EventEmailsSetting> {
    const res = await api.api['business-users'].me['event-emails'].$get()
    return unwrap<EventEmailsSetting>(res)
  },

  async setEventEmails(enabled: boolean): Promise<EventEmailsSetting> {
    const res = await api.api['business-users'].me['event-emails'].$put({ json: { enabled } })
    return unwrap<EventEmailsSetting>(res)
  },
}
